import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthContext } from '../../../hooks/context/authContext';

import { useManagePutRequest } from '../../../hooks/useManagePutRequest/useManagePutRequest';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { useEvaluacionCombos } from '../../../hooks/useEvaluacionCombos/useEvaluacionCombos';

import { 
  getEvaluacionByIdAPI, 
  editarEvaluacionAPI, 
  updateCargosEvaluacionAPI,
  crearEvaluacionAPI,
  opcionesCrudAPI,
  preguntasCrudAPI 
} from '../../../consumers/backendApisUrls';

import useEvaluacionForm from './Hooks/useEvaluacionForm';
import useEvaluacionValidation from './Hooks/useEvaluacionValidation';
import { usePreguntasOperations } from './Steps/Step3Preguntas/hooks/usePreguntasOperations';

import StepIndicator from './UI/StepIndicator';
import NavigationButtons from './UI/NavigationButtons';
import ConfirmacionCancelarModal from './UI/ConfirmacionCancelarModal';
import ConfirmacionActualizarModal from './UI/ConfirmacionActualizarModal';
import { LoadingState, ErrorState } from './UI/StateComponents';

import Step1InformacionBasica from './Steps/Step1InformacionBasica/Step1InformacionBasica';
import Step2Configuracion from './Steps/Step2Configuracion/Step2Configuracion';
import Step3Preguntas from './Steps/Step3Preguntas/Step3Preguntas';
import Step4Revision from './Steps/Step4Revision/Step4Revision';
import SidebarProcesosSeleccion from '../../../components/pageComponents/SidebarProcesosSeleccion';

import { 
  mapearEvaluacionDesdeAPI, 
  mapearEvaluacionParaEnvio,
  obtenerNumeroOriginal 
} from './Utils/dataMappers';
import { STEPS, VALIDATION_LIMITS, DEFAULT_VALUES } from './Utils/constants';
import './EditarEvaluacion.css';

// Mapeo validado en BD INTRANETCARGA (tabla SubtipoPruebaPsicologica).
const SUBTIPO_PSICOLOGICO_ID_MAP = {
  personalidad: 1,
  salamanca: 2
};

const normalizarSubtipo = (valor = '') =>
  {
    const normalizado = valor
      .toString()
      .trim()
      .toLowerCase();

    if (['dass-21', 'dass21', 'dass'].includes(normalizado)) {
      return 'personalidad';
    }

    return ['salamanca', 'personalidad'].includes(normalizado) ? normalizado : '';
  };

const getIdSubtipoPsicologico = (subtipo) => {
  const clave = normalizarSubtipo(subtipo);
  return SUBTIPO_PSICOLOGICO_ID_MAP[clave] || null;
};

const getSubtipoPsicologicoById = (idSubtipo) => {
  if (idSubtipo === 1) return 'personalidad';
  if (idSubtipo === 2) return 'salamanca';
  return '';
};

const withSidebarLayout = (content) => (
  <div className="app-wrapper">
    <div className="no-disponible-en-mobile">
      <div style={{ display: 'flex', alignItems: 'stretch', position: 'relative' }}>
        <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: '130px', overflowY: 'auto', zIndex: 100 }}>
          <SidebarProcesosSeleccion />
        </div>
        <div style={{ flex: 1, minWidth: 0, marginLeft: '130px' }}>
          {content}
        </div>
      </div>
    </div>
  </div>
);

const EditarEvaluacion = () => {
  const history = useHistory();
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get('id');
  const { state } = useContext(AuthContext);
  const currentUser = state?.user?.Usuario || state?.user?.email || 'sistema';
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [step3HasPendingChanges, setStep3HasPendingChanges] = useState(false);
  const [evaluacionData, setEvaluacionData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [showConfirmacionCancelar, setShowConfirmacionCancelar] = useState(false);
  const [showConfirmacionActualizar, setShowConfirmacionActualizar] = useState(false);
  
  const {
    formData,
    setFormData,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleCargoChange,
    handleMarcarTodosCargos,
    updateFormData
  } = useEvaluacionForm();

  const { errors, validateStep, clearError } = useEvaluacionValidation();

  const [managePutRequest] = useManagePutRequest();
  const [managePostRequest] = useManagePostRequest();
  const [executeGetRequest] = useGetRequest();

  // Para guardar preguntas y opciones via CRUDs después de crear la evaluación
  const { guardarTodoEnBD } = usePreguntasOperations(null);

  const { 
    combos, 
    loading: combosLoading, 
    loadingCargos,
    cargarCargosPorTipo,
    getTipoEvaluacionById, 
    getTipoCargoById 
  } = useEvaluacionCombos();

  const ultimoTipoCargoCargado = useRef(null);
  const isMounted = useRef(true);
  const isCargandoEvaluacion = useRef(false);

  const isEditMode = !!id;
  
  const tiposPrueba = combos.tiposEvaluacion || [];
  const tiposCargo = combos.tiposCargo || [];
  const cargosEspecificos = combos.cargos || [];

  const normalizarTexto = (texto = '') =>
    texto
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const tipoPruebaSeleccionado = tiposPrueba.find(tp => String(tp.value) === String(formData.tipoPrueba));
  const esTipoPruebaPsicologica = normalizarTexto(tipoPruebaSeleccionado?.label || tipoPruebaSeleccionado?.nombre || '').includes('psicolog');
  const isPsicologicaSalamanca = esTipoPruebaPsicologica && ['salamanca', 'personalidad', 'dass-21', 'dass21', 'dass'].includes(formData.subtipoPruebaPsicologica);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (id && !combosLoading && combos.tiposEvaluacion.length > 0 && combos.tiposCargo.length > 0) {
      isCargandoEvaluacion.current = true;
      cargarEvaluacionReal();
    }
  }, [id, combosLoading, combos.tiposEvaluacion.length, combos.tiposCargo.length]);

  useEffect(() => {
    if (formData.idTipoCargo && formData.idTipoCargo !== ultimoTipoCargoCargado.current) {
      console.log('🔄 Tipo de cargo cambió, cargando cargos específicos:', {
        nuevoTipo: formData.idTipoCargo,
        ultimoCargado: ultimoTipoCargoCargado.current,
        isCargandoEvaluacion: isCargandoEvaluacion.current,
        cargosActuales: formData.cargosSeleccionados
      });
      
      ultimoTipoCargoCargado.current = formData.idTipoCargo;
      cargarCargosPorTipo(formData.idTipoCargo);
      
      if (!isCargandoEvaluacion.current) {
        console.log('🧹 Limpiando cargos seleccionados por cambio de tipo de cargo');
        setFormData(prev => ({ ...prev, cargosSeleccionados: [] }));
      } else {
        console.log('⏭️ Preservando cargos seleccionados durante carga inicial:', formData.cargosSeleccionados);
      }
      
    } else if (!formData.idTipoCargo && ultimoTipoCargoCargado.current) {
      console.log('🗑️ Tipo de cargo removido, limpiando referencia');
      ultimoTipoCargoCargado.current = null;
    }
  }, [formData.idTipoCargo]);

  useEffect(() => {
    const puntajeTotal = formData.preguntas.reduce((total, pregunta) => {
      return total + (pregunta.opciones?.reduce((suma, opcion) => suma + (opcion.puntaje || 0), 0) || 0);
    }, 0);
    
    if (formData.puntajeTotal !== puntajeTotal) {
      setFormData(prev => ({
        ...prev,
        puntajeTotal
      }));
    }
  }, [formData.preguntas]);

  const mapearOpciones = (opcionesApi) => {
    if (!Array.isArray(opcionesApi)) {
      return [];
    }

    const opcionesMapeadas = opcionesApi.map((opcion, index) => {
      const opcionMapeada = {
        id: opcion.idOpcionRespuesta || opcion.idOpcion || `opcion_${index + 1}`,
        texto: opcion.textoOpcion || opcion.texto || '',
        esCorrecta: opcion.esCorrecta || false,
        puntaje: opcion.puntaje || 0
      };
      
      // Log para debug si hay discrepancia
      if (opcion.idOpcionRespuesta && !opcion.idOpcion) {
        console.log('📝 Opción mapeada (backend usa idOpcionRespuesta):', {
          backend: { idOpcionRespuesta: opcion.idOpcionRespuesta, textoOpcion: opcion.textoOpcion },
          frontend: { id: opcionMapeada.id, texto: opcionMapeada.texto }
        });
      }
      
      return opcionMapeada;
    });
    
    return opcionesMapeadas;
  };

  const mapearPreguntas = (preguntasApi) => {
    if (!Array.isArray(preguntasApi)) {
      console.log('⚠️ No se encontraron preguntas en la API');
      return [];
    }
    
    console.log('🔄 Mapeando', preguntasApi.length, 'preguntas desde API');
    
    return preguntasApi.map((pregunta, index) => {
      const preguntaMapeada = {
        id: pregunta.idEvaluacionPregunta || `pregunta_${index + 1}`,
        pregunta: pregunta.textoPregunta || '',
        numero: pregunta.numero || (index + 1),
        esObligatoria: pregunta.esObligatoria !== undefined ? pregunta.esObligatoria : true,
        puntaje: 0,
        opciones: mapearOpciones(pregunta.opciones || [])
      };
      
      preguntaMapeada.puntaje = preguntaMapeada.opciones.reduce((total, opcion) => {
        return total + (opcion.puntaje || 0);
      }, 0);
      
      return preguntaMapeada;
    });
  };

  const cargarDatosDesdeBD = async () => {
    try {
      const evaluacionDelListado = {
        nombreEvaluacion: 'Evaluación ID ' + id + ' (Modo Degradado)',
        tipoPrueba: 'Examen',
        idTipoCargo: '',
        tiempoLimite: 60,
        puntajeTotal: 100,
        puntajeAprobatorio: 0,
        pesoCalificacion: 1,
        esObligatoria: true,
        encabezadoDescripcion: 'Evaluación cargada en modo degradado',
        instrucciones: 'El backend tiene problemas de serialización. Puede editar normalmente.',
        cargosSeleccionados: [],
        preguntas: [
          {
            id: 'temp_1',
            pregunta: 'Pregunta de ejemplo (añada sus preguntas reales)',
            numero: 1,
            esObligatoria: true,
            puntaje: 10,
            opciones: [
              { id: 'temp_1_a', texto: 'Opción A', esCorrecta: true, puntaje: 10 },
              { id: 'temp_1_b', texto: 'Opción B', esCorrecta: false, puntaje: 0 }
            ]
          }
        ]
      };
      
      setFormData(prev => ({ ...prev, ...evaluacionDelListado }));
      console.log('✅ Datos degradados cargados correctamente');
      
    } catch (error) {
      console.error('❌ Error al cargar datos degradados:', error);
    }
  };

  const activarModoDegradado = () => {
    console.log('🚑 Activando modo degradado para evaluación ID:', id);
    cargarDatosDesdeBD();
    
    setErrorData(
      `⚠️ MODO DEGRADADO ACTIVADO\n\n` +
      `El backend tiene problemas de serialización JSON.\n` +
      `Se cargaron datos básicos desde la base de datos.\n\n` +
      `• Puede editar la evaluación normalmente\n` +
      `• Los cambios se guardarán correctamente\n` +
      `• Contacte al equipo técnico para solucionar el backend`
    );
  };

  const guardarCargosSeleccionados = async (evaluacionId, cargos) => {
    try {
      console.log('💾 Sincronizando cargos seleccionados para evaluación:', { evaluacionId, cargos });
      
      // Convertir array a string CSV para el backend (SP espera VARCHAR separado por comas)
      const cargosArray = Array.isArray(cargos) ? cargos : [];
      const idCargosString = cargosArray.length > 0 ? cargosArray.join(',') : null;
      
      console.log('🔄 Conversión de cargos:');
      console.log('  - Array original:', cargosArray);
      console.log('  - String CSV:', idCargosString);
      
      const dataCargos = {
        idEvaluacion: parseInt(evaluacionId),
        idCargos: idCargosString,  // Backend espera "idCargos" como VARCHAR: "605,1072"
        usuario: currentUser
      };
      
      console.log('📤 Llamando a UpdateCargos con payload:', dataCargos);
      console.log('🔗 Endpoint:', updateCargosEvaluacionAPI());
      
      await managePostRequest(
        updateCargosEvaluacionAPI(),
        dataCargos,
        (response) => {
          if (isMounted.current) {
            console.log('✅ Cargos sincronizados exitosamente:', response);
            console.log('✅ Respuesta del backend:', response.data);
          }
        },
        false, // showToastSuccess = false (evita toast duplicado)
        (error) => {
          console.error('❌ Error al sincronizar cargos:', error);
          console.error('  - Status:', error.response?.status);
          console.error('  - Data:', error.response?.data);
          throw error;
        },
        false // showToastError = false (el error se maneja en el callback)
      );
      
      return Promise.resolve();
    } catch (error) {
      console.error('❌ Error al guardar cargos:', error);
      throw error;
    }
  };

  const intentarGuardadoSimplificado = async (dataOriginal, evaluacionId) => {
    try {
      const dataSimplificada = {
        idEvaluacion: parseInt(evaluacionId),
        nombreEvaluacion: dataOriginal.nombreEvaluacion,
        tiempoLimite: dataOriginal.tiempoLimite,
        puntajeMaximo: dataOriginal.puntajeMaximo,
        puntajeAprobacion: 0,
        pesoCalificacion: dataOriginal.pesoCalificacion,
        esObligatoria: dataOriginal.esObligatoria,
        estado: dataOriginal.estado,
        encabezado: dataOriginal.encabezado,
        instrucciones: dataOriginal.instrucciones,
        idTipoCargo: dataOriginal.idTipoCargo,
        idTipoEvaluacion: dataOriginal.idTipoEvaluacion,
        usuario: currentUser
      };

      console.log('🔄 Reintentando con estructura simplificada:', dataSimplificada);

      await managePostRequest(
        editarEvaluacionAPI(),
        dataSimplificada,
        (response) => {
          if (isMounted.current) {
            console.log('✅ Evaluación guardada con estructura simplificada:', response);
            console.log(`✅ Evaluación actualizada exitosamente - ID: ${evaluacionId} - Datos guardados con estructura simplificada`);
            history.push('/evaluacionesProcesos');
          }
        },
        false,
        (error) => {
          if (isMounted.current) {
            console.error('❌ Error persistente al guardar la evaluación:', error);
            console.error(`❌ Detalles técnicos: ${error.message}`);
          }
        }
      );
    } catch (error) {
      console.error('❌ Error crítico al intentar guardado simplificado:', error);
    }
  };

  const cargarEvaluacionReal = useCallback(async () => {
    if (!id) return;
    
    setLoadingData(true);
    setErrorData(null);
    
    try {
      console.log('🔍 Cargando evaluación real ID:', id);
      
      const response = await executeGetRequest(getEvaluacionByIdAPI(id));
      
      let evaluacionApi = null;
      
      if (response?.data?.Data) {
        evaluacionApi = response.data.Data;
      } else if (response?.data?.data?.Data) {
        evaluacionApi = response.data.data.Data;
      } else if (response?.data?.data) {
        evaluacionApi = response.data.data;
      } else if (response?.data) {
        evaluacionApi = response.data;
      }
      
      if (evaluacionApi && (evaluacionApi.idEvaluacion || evaluacionApi.nombreEvaluacion)) {
        console.log('✅ Datos de evaluación encontrados');
        
        const tipoEvaluacionEncontrado = getTipoEvaluacionById(evaluacionApi.idTipoEvaluacion || evaluacionApi.idTipoEval);
        const tipoCargoEncontrado = getTipoCargoById(evaluacionApi.idTipoCargo);
        
        const subtipoDesdeApi =
          evaluacionApi.subtipoPruebaPsicologica ||
          evaluacionApi.codigoSubtipoPruebaPsicologica ||
          getSubtipoPsicologicoById(evaluacionApi.idSubtipoPruebaPsicologica);

        const evaluacionMapeada = {
          nombreEvaluacion: evaluacionApi.nombreEvaluacion || '',
          tipoPrueba: tipoEvaluacionEncontrado?.value || evaluacionApi.idTipoEvaluacion || evaluacionApi.idTipoEval || '',
          subtipoPruebaPsicologica: normalizarSubtipo(subtipoDesdeApi),
          idTipoCargo: tipoCargoEncontrado?.value || evaluacionApi.idTipoCargo || '',
          tiempoLimite: evaluacionApi.tiempoLimite || evaluacionApi.tiempoLimiteMinutos || 60,
          puntajeTotal: evaluacionApi.puntajeMaximo || 0,
          puntajeAprobatorio: 0,
          pesoCalificacion: evaluacionApi.pesoCalificacion || 0,
          esObligatoria: true,
          estadoActivo: evaluacionApi.estado !== undefined ? evaluacionApi.estado : true,
          encabezadoDescripcion: evaluacionApi.encabezado ||
                                evaluacionApi.encabezadoDescripcion || 
                                evaluacionApi.descripcion || 
                                evaluacionApi.descripcionCorta || '',
          instrucciones: evaluacionApi.instrucciones || '',
          cargosSeleccionados: evaluacionApi.cargosSeleccionados || 
                              evaluacionApi.idsCargos || 
                              evaluacionApi.cargos ||
                              evaluacionApi.listaCargos ||
                              evaluacionApi.cargoIds || [],
          preguntas: mapearPreguntas(evaluacionApi.preguntas || [])
        };
        
        evaluacionMapeada.puntajeTotal = evaluacionMapeada.preguntas.reduce((total, preg) => total + (preg.puntaje || 0), 0);
        
        // Establecer todos los datos de una vez
        setFormData(prev => ({ ...prev, ...evaluacionMapeada }));
        
        // Cargar cargos específicos si es necesario
        if (evaluacionMapeada.idTipoCargo) {
          ultimoTipoCargoCargado.current = evaluacionMapeada.idTipoCargo;
          cargarCargosPorTipo(evaluacionMapeada.idTipoCargo);
        }
        
        isCargandoEvaluacion.current = false;
        console.log('✅ Carga inicial completada');
        
      } else {
        console.error('❌ Respuesta sin datos válidos de evaluación');
        setErrorData(`No se encontraron datos válidos de evaluación.`);
      }
    } catch (error) {
      console.error('❌ Error crítico al cargar evaluación:', error);
      
      if (error.response?.status === 500 && 
          (error.response?.data?.Message === 'Invalid JSON response' || 
           error.message.includes('Invalid JSON response'))) {
        
        console.warn('⚠️ Error conocido del backend - Activando modo degradado');
        activarModoDegradado();
        
      } else {
        setErrorData(
          `Error del servidor: ${error.response?.status || 'desconocido'}\n\n` +
          `No se pudieron cargar los datos originales de la evaluación ID ${id}.`
        );
      }
    } finally {
      setLoadingData(false);
    }
  }, [id, executeGetRequest, combos.tiposEvaluacion, combos.tiposCargo, getTipoEvaluacionById, getTipoCargoById]);

  const nextStep = () => {
    if (validateStep(currentStep, formData, { tiposPrueba }) && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleToggleEstado = useCallback(() => {
    const nuevoEstado = !formData.estadoActivo;
    setFormData(prev => ({ ...prev, estadoActivo: !prev.estadoActivo }));
    toast.dismiss('eval-estado');
    if (nuevoEstado) {
      toast.success('La evaluación está activa y disponible para su envío.', { toastId: 'eval-estado', autoClose: 5000 });
    } else {
      toast.warning('La evaluación está inactiva y no estará disponible para asignar a postulantes.', { toastId: 'eval-estado', autoClose: 5000 });
    }
  }, [formData.estadoActivo, setFormData]);

  const prevStep = () => {
    // Si hay cambios pendientes en el paso 3, mostrar confirmación nativa
    if (step3HasPendingChanges) {
      if (!window.confirm('Hay preguntas sin guardar en este paso. ¿Desea retroceder y perder los cambios?')) {
        return;
      }
    }

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancelClick = () => {
    // Si hay cambios pendientes en el paso 3, mostrar confirmación nativa
    if (step3HasPendingChanges) {
      if (window.confirm('Hay preguntas sin guardar. Si sale ahora, perderá los cambios. ¿Desea continuar?')) {
        history.push('/evaluacionesProcesos');
      }
      return;
    }

    // Mostrar modal de confirmación solo en pasos 1 y 4
    if (currentStep === 1 || currentStep === 4) {
      setShowConfirmacionCancelar(true);
    } else {
      // En pasos intermedios, cancelar sin confirmación
      history.push('/evaluacionesProcesos');
    }
  };

  const handleConfirmarCancelar = () => {
    setShowConfirmacionCancelar(false);
    history.push('/evaluacionesProcesos');
  };

  const handleSubmitClick = () => {
    // Mostrar modal de confirmación solo en modo editar y en paso 4
    if (isEditMode && currentStep === 4) {
      setShowConfirmacionActualizar(true);
    } else {
      // En modo crear o en otros pasos, enviar directamente
      handleSubmit();
    }
  };

  const handleConfirmarActualizar = async () => {
    setShowConfirmacionActualizar(false);
    await handleSubmit();
  };

  const handleCancel = () => {
    history.push('/evaluacionesProcesos');
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep, formData, { tiposPrueba })) {
      return;
    }

    // Validaciones adicionales antes de enviar
    if (!formData.nombreEvaluacion?.trim()) {
      console.error('❌ El nombre de la evaluación es obligatorio');
      return;
    }
    
    if (!formData.tipoPrueba) {
      console.error('❌ Debe seleccionar un tipo de evaluación');
      console.error('   formData.tipoPrueba actual:', formData.tipoPrueba);
      return;
    }
    
    if (!formData.idTipoCargo) {
      console.error('❌ Debe seleccionar un tipo de cargo');
      console.error('   formData.idTipoCargo actual:', formData.idTipoCargo);
      return;
    }

    if (formData.preguntas && formData.preguntas.length > 0) {
      const preguntasSinOpciones = formData.preguntas.filter(p => !p.opciones || p.opciones.length === 0);
      if (preguntasSinOpciones.length > 0) {
        console.error('❌ Hay preguntas sin opciones válidas:', preguntasSinOpciones.length);
        alert('Todas las preguntas deben tener al menos una opción con texto y puntaje.');
        return;
      }
    }

    console.log('✅ Validaciones pasadas. Procediendo a guardar...');

    setIsLoading(true);
    
    try {
      if (isEditMode) {
        const limpiarString = (valor) => {
          if (typeof valor === 'string') {
            return valor.replace(/^["']|["']$/g, '');
          }
          return valor || '';
        };

        const limpiarNumero = (valor, limite = 999999, minimo = 0) => {
          if (typeof valor === 'string') {
            const limpio = valor.replace(/^["']|["']$/g, '');
            let numero = parseFloat(limpio) || 0;
            numero = Math.max(minimo, Math.min(limite, numero));
            return Math.floor(numero);
          }
          let numero = parseFloat(valor) || 0;
          numero = Math.max(minimo, Math.min(limite, numero));
          return Math.floor(numero);
        };

        const idSubtipoPruebaPsicologica = esTipoPruebaPsicologica
          ? getIdSubtipoPsicologico(formData.subtipoPruebaPsicologica)
          : null;
        const cargosSeleccionadosNormalizados = (formData.cargosSeleccionados || []).map(cargoId => parseInt(cargoId));

        const dataToSubmit = {
          idEvaluacion: parseInt(id),
          nombreEvaluacion: limpiarString(formData.nombreEvaluacion).trim(),
          tiempoLimite: limpiarNumero(formData.tiempoLimite, 43200),
          puntajeMaximo: formData.puntajeTotal || 0,
          puntajeAprobacion: 0,
          pesoCalificacion: formData.pesoCalificacion === '' || formData.pesoCalificacion === null ? null : limpiarNumero(formData.pesoCalificacion, 100, 0),
          esObligatoria: true,
          estado: Boolean(formData.estadoActivo),
          encabezado: limpiarString(formData.encabezadoDescripcion),
          instrucciones: limpiarString(formData.instrucciones),
          idTipoCargo: parseInt(formData.idTipoCargo),
          idTipoEvaluacion: parseInt(formData.tipoPrueba),
          idSubtipoPruebaPsicologica,
          cargos: cargosSeleccionadosNormalizados,
          usuario: currentUser
        };
        
        console.log('💾 Guardando evaluación con estructura correcta');
        
        await managePostRequest(
          editarEvaluacionAPI(),
          dataToSubmit,
          async (response) => {
            if (isMounted.current) {
              toast.success('Se actualizó la evaluación correctamente');
              console.log('✅ Evaluación actualizada exitosamente');
              
              // Sincronizar cargos después de actualizar la evaluación
              if (cargosSeleccionadosNormalizados.length > 0) {
                try {
                  await guardarCargosSeleccionados(id, cargosSeleccionadosNormalizados);
                  console.log(`✅ Cargos sincronizados exitosamente`);
                } catch (cargoError) {
                  console.warn('⚠️ Error al sincronizar cargos:', cargoError);
                }
              }
              
              history.push('/evaluacionesProcesos');
            }
          },
          false, // Desactivar toast automático
          async (error) => {
            if (isMounted.current) {
              console.error('❌ Error al actualizar evaluación:', error);
              
              if (error.response?.status === 400) {
                const dataSinCargos = { ...dataToSubmit };
                delete dataSinCargos.cargos;
                
                await managePostRequest(
                  editarEvaluacionAPI(),
                  dataSinCargos,
                  (response) => {
                    if (isMounted.current) {
                      toast.success('Se actualizó la evaluación correctamente');
                      console.log('✅ Evaluación guardada sin cargos');
                      history.push('/evaluacionesProcesos');
                    }
                  },
                  false,
                  (error2) => {
                    if (isMounted.current) {
                      console.error('❌ Error persistente:', error2);
                    }
                  }
                );
              }
            }
          }
        );
      } else {
        // CREAR NUEVA EVALUACIÓN
        const tipoSeleccionado = tiposPrueba.find(t => 
          t.value === formData.tipoPrueba || t.value === parseInt(formData.tipoPrueba)
        );
        
        let idTipoPrueba = tipoSeleccionado?.id || parseInt(formData.tipoPrueba) || 1;
        const esCreatePsicologica = normalizarTexto(tipoSeleccionado?.label || tipoSeleccionado?.nombre || '').includes('psicolog');
        const idSubtipoPruebaPsicologica = esCreatePsicologica
          ? getIdSubtipoPsicologico(formData.subtipoPruebaPsicologica)
          : null;
        
        // Usar MAX por pregunta: para psicológicas (DASS-21) con EsCorrecta=1 en todas
        // las opciones, la suma acumularía 0+1+2+3=6 por pregunta en vez del máximo real (3)
        const puntajeMaximoCalculado = formData.preguntas.reduce((total, pregunta) => {
          if (pregunta.opciones && pregunta.opciones.length > 0) {
            const puntajePregunta = pregunta.opciones
              .filter(opcion => opcion.esCorrecta)
              .reduce((max, opcion) => Math.max(max, parseFloat(opcion.puntaje) || 0), 0);
            return total + puntajePregunta;
          }
          return total;
        }, 0);

        // Creación: Se envía la evaluación completa con preguntas
        // Se mapean las preguntas al formato esperado por el backend para evitar "Sin información"
        const preguntasMapped = formData.preguntas.map(p => ({
          textoPregunta: p.pregunta,
          esObligatoria: p.esObligatoria,
          numero: p.numero,
          puntaje: p.opciones.filter(o => o.esCorrecta).reduce((sum, o) => sum + (parseFloat(o.puntaje) || 0), 0),
          opciones: p.opciones.map(o => ({
            textoOpcion: o.texto,
            esCorrecta: o.esCorrecta,
            puntajeOpcion: parseFloat(o.puntaje) || 0,
            ordenOpcion: 0 // El backend asigna orden o se puede enviar indice
          }))
        }));

        const dataToSubmit = {
          nombreEvaluacion: formData.nombreEvaluacion.trim(),
          encabezadoDescripcion: formData.encabezadoDescripcion?.trim() || null,
          idTipoPrueba: parseInt(idTipoPrueba),
          idSubtipoPruebaPsicologica,
          idTipoCargo: parseInt(formData.idTipoCargo),
          instrucciones: formData.instrucciones?.trim() || null,
          tiempoLimite: parseInt(formData.tiempoLimite) || 0,
          puntajeAprobatorio: 0,
          puntajeAprobacion: 0,
          puntajeMaximo: parseFloat(puntajeMaximoCalculado.toString()),
          pesoCalificacion: formData.pesoCalificacion === '' || formData.pesoCalificacion === null ? null : parseFloat(formData.pesoCalificacion.toString()),
          esObligatoria: true,
          estadoActivo: Boolean(formData.estadoActivo),
          intentosPermitidos: parseInt(formData.intentosPermitidos) || 2,
          usuario: currentUser,
          preguntas: preguntasMapped,  // Enviamos las preguntas directamente
          cargos: (formData.cargosSeleccionados || []).map(id => parseInt(id))
        };
        
        console.log('💾 Creando nueva evaluación (completa)');
        console.log('  - Nombre:', dataToSubmit.nombreEvaluacion);
        console.log('  - idTipoPrueba:', dataToSubmit.idTipoPrueba);
        console.log('  - tiempoLimite:', dataToSubmit.tiempoLimite);
        console.log('  - Preguntas enviadas:', dataToSubmit.preguntas.length);
        
        await managePostRequest(
          crearEvaluacionAPI(),
          dataToSubmit,
          (response) => {
            if (!isMounted.current) return;
            toast.success('Se creó la evaluación exitosamente');
            history.push('/evaluacionesProcesos');
          },
          false,   // showToastSuccess: lo manejamos manualmente
          (error) => {
            console.error('❌ Error al crear evaluación:', error?.data);
          },
          true
        );
      }
    } catch (error) {
      if (isMounted.current) {
        console.error('Error inesperado:', error);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  const renderStepContent = () => {
    switch(currentStep) {
      case STEPS.INFORMACION_BASICA:
        return (
          <Step1InformacionBasica
            formData={formData}
            errors={errors}
            tiposPrueba={tiposPrueba}
            tiposCargo={tiposCargo}
            cargosEspecificos={cargosEspecificos}
            loadingCargos={loadingCargos}
            handleInputChange={(e) => {
              handleInputChange(e);
              clearError(e.target.name);
            }}
            handleSelectChange={(e) => {
              handleSelectChange(e);
              if (e?.target?.name === 'tipoPrueba') {
                const selectedValue = e?.target?.value?.value;
                const selectedTipo = tiposPrueba.find(tp => String(tp.value) === String(selectedValue));
                const esPsicologica = normalizarTexto(selectedTipo?.label || selectedTipo?.nombre || '').includes('psicolog');

                if (!esPsicologica) {
                  setFormData(prev => ({ ...prev, subtipoPruebaPsicologica: '' }));
                  clearError('subtipoPruebaPsicologica');
                }
              }
              clearError(e.target.name);
            }}
            handleSubtipoPsicologicaChange={(subtipo) => {
              setFormData(prev => ({ ...prev, subtipoPruebaPsicologica: subtipo }));
              clearError('subtipoPruebaPsicologica');
            }}
            handleCargoChange={handleCargoChange}
            handleMarcarTodosCargos={() => handleMarcarTodosCargos(cargosEspecificos)}
          />
        );

      case STEPS.CONFIGURACION:
        return (
          <Step2Configuracion
            formData={formData}
            errors={errors}
            handleInputChange={(e) => {
              handleInputChange(e);
              clearError(e.target.name);
            }}
            setFormData={setFormData}
            onToggleEstado={handleToggleEstado}
          />
        );

      case STEPS.PREGUNTAS:
        return (
          <Step3Preguntas
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            clearError={clearError}
            isEditMode={isEditMode}
            evaluacionId={id}
            onChangesPending={setStep3HasPendingChanges}
            isPsicologicaSalamanca={isPsicologicaSalamanca}
          />
        );

      case STEPS.REVISION:
        return (
          <Step4Revision
            formData={formData}
            tiposPrueba={tiposPrueba}
            tiposCargo={tiposCargo}
            cargosEspecificos={cargosEspecificos}
            isEditMode={isEditMode}
            onEdit={(step) => setCurrentStep(step)}
          />
        );

      default:
        return null;
    }
  };

  if (combosLoading) {
    return withSidebarLayout(
      <LoadingState 
        message=""
        subtitle=""
        color="info"
        size="large"
      />
    );
  }

  if (loadingData) {
    return withSidebarLayout(
      <LoadingState 
        message=""
        subtitle=""
        color="primary"
        size="large"
      />
    );
  }

  if (errorData && isEditMode) {
    const isModoDegradado = errorData.includes('MODO DEGRADADO');
    
    return withSidebarLayout(
      <ErrorState
        title={isModoDegradado ? 'Modo Degradado Activado' : 'Error al cargar datos reales'}
        message={errorData}
        type={isModoDegradado ? 'warning' : 'danger'}
        actions={
          <div>
            {isModoDegradado ? (
              <button 
                className="btn btn-success me-2" 
                onClick={() => setErrorData(null)}
              >
                <i className="fas fa-play me-1"></i>
                Continuar Editando (Modo Degradado)
              </button>
            ) : (
              <button 
                className="btn btn-primary me-2" 
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-redo me-1"></i>
                Reintentar carga
              </button>
            )}
            
            <button 
              className="btn btn-secondary" 
              onClick={() => history.push('/evaluacionesProcesos')}
            >
              <i className="fas fa-arrow-left me-1"></i>
              Volver a Evaluaciones
            </button>
          </div>
        }
      />
    );
  }

  return withSidebarLayout(
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="evaluacion-sticky-shell">
              <div className="card-header bg-primary text-white">
                <h4 className="mb-0">
                  <i className={`fas ${isEditMode ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                  {isEditMode ? 'Editar Evaluación' : 'Crear Nueva Evaluación'}
                </h4>
                <small>
                  {isEditMode 
                    ? '' 
                    : 'Completa todos los pasos para crear la evaluación'
                  }
                </small>
              </div>

              <div className="evaluacion-steps-shell">
                <StepIndicator currentStep={currentStep} totalSteps={4} />
              </div>
            </div>
            
            <div className="card-body">
              <div className="step-content" style={{ minHeight: '400px' }}>
                {renderStepContent()}
              </div>

              <ConfirmacionCancelarModal
                show={showConfirmacionCancelar}
                currentStep={currentStep}
                totalSteps={4}
                onConfirm={handleConfirmarCancelar}
                onCancel={() => setShowConfirmacionCancelar(false)}
              />

              <ConfirmacionActualizarModal
                show={showConfirmacionActualizar}
                isLoading={isLoading}
                onConfirm={handleConfirmarActualizar}
                onCancel={() => setShowConfirmacionActualizar(false)}
              />

              <NavigationButtons
                currentStep={currentStep}
                totalSteps={4}
                isLoading={isLoading}
                isEditMode={isEditMode}
                onPrevious={prevStep}
                onNext={nextStep}
                onCancel={handleCancel}
                onCancelClick={handleCancelClick}
                onSubmitClick={handleSubmitClick}
                onSubmit={handleSubmit}
                showCancelButton={true}
                hasPendingChanges={isEditMode && currentStep === STEPS.PREGUNTAS && step3HasPendingChanges}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditarEvaluacion;